import * as React from "react";
import { Typography, Box, CircularProgress, Alert } from "@mui/material";
import Mainlayout from "../../../components/layouts/MainLayout";
import { useJyloDocument, DocumentStatus } from "../hooks/useJyloDocument";
import PlaybookAssemblyWrapper from "./PlaybookAssemblyWrapper";
import SaveAssemblyButton from "./SaveAssemblyButton";
import { AssemblyApiContext } from "../types/assembly-context";

interface ReferencesProps {
  headerLeft?: React.ReactNode;
}

/** Builds the API context from a detected document, or null if it is not a usable Jylo assembly. */
const buildAssemblyContext = (status: DocumentStatus): AssemblyApiContext | null => {
  if (!status.isJylo || !status.source || !status.assemblyObjectIdentifier) return null;

  if (status.source === "playbook" && status.playbookObjectIdentifier) {
    return {
      source: "playbook",
      assemblyObjectIdentifier: status.assemblyObjectIdentifier,
      playbookObjectIdentifier: status.playbookObjectIdentifier,
    };
  }

  if (status.source === "flow" && status.projectObjectIdentifier && status.flowObjectIdentifier) {
    return {
      source: "flow",
      assemblyObjectIdentifier: status.assemblyObjectIdentifier,
      projectObjectIdentifier: status.projectObjectIdentifier,
      flowObjectIdentifier: status.flowObjectIdentifier,
    };
  }

  return null;
};

const References = ({ headerLeft }: ReferencesProps) => {
  const { documentStatus, isLoading: checkingDocumentStatus } = useJyloDocument();

  const assemblyContext = React.useMemo(
    () => buildAssemblyContext(documentStatus),
    [documentStatus]
  );

  return (
    <Mainlayout
      headerLeft={headerLeft}
      headerButtons={<SaveAssemblyButton ctx={assemblyContext} size="small" variant="contained" />}
    >
      {checkingDocumentStatus && (
        <Box>
          <Typography>Loading document status...</Typography>
          <CircularProgress />
        </Box>
      )}
      {assemblyContext && <PlaybookAssemblyWrapper ctx={assemblyContext} />}
      {documentStatus.checked && !assemblyContext && (
        <Alert severity="warning" sx={{ m: 2 }}>
          This is not a Jylo Assembly document. Please open a Jylo document to use the reference
          features.
        </Alert>
      )}
    </Mainlayout>
  );
};

export default References;
